import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { UseQuoteDetailReturn } from '../../hooks/useQuoteDetail';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { format } from 'date-fns/format';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Clock, Download } from 'lucide-react';
import { formatPrice } from '../../lib/currency';
import { ROUTE_ARROW, formatMeasurement, formatQuoteRequestDate, isUnspecifiedDateType } from '../../lib/format';

interface QuoteDetailProps extends UseQuoteDetailReturn {}

// The API field cargo_volume_m3 is already expressed in cubic metres (a
// 100×80×60 cm crate arrives as 0.48). Render it directly, without conversion.
const formatCargoVolume = (raw: number | null | undefined): string => {
  if (raw === null || raw === undefined) return '';
  // Avoid scientific notation for tiny values and strip trailing zeros.
  return Number(raw).toLocaleString(undefined, {
    maximumFractionDigits: 6,
    useGrouping: false,
  });
};

const formatScheduleHour = (hour: number | string | null | undefined): string | null => {
  if (hour === null || hour === undefined || hour === '') return null;
  const num = Number(hour);
  if (Number.isNaN(num) || num < 0 || num > 23) return null;
  return `${String(num).padStart(2, '0')}:00`;
};

export const QuoteDetail: React.FC<QuoteDetailProps> = (props) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language;

  const {
    quote,
    loading,
    imageModalOpen,
    setImageModalOpen,
    setCurrentImageIndex,
    currentImageIndex,
    contactingId,
    downloadingResponseId,
    expandedIds,
    toggleExpanded,
    nextImage,
    prevImage,
    handleContactCarrier,
    handleDownloadPdf,
    pickupDate,
    orderAge,
    companiesResponded,
    companiesContacted,
    pendingCount,
    sortedResponses,
    getStatusBadgeVariant,
    getStatusBadgeClass,
    isExpiringSoon,
    isExpired,
  } = props;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-12 w-96" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!quote) {
    return <div>{t('quote.detail.not_found')}</div>;
  }

  const originHour = formatScheduleHour(quote.origin_fixed_hour);
  const destinationHour = formatScheduleHour(quote.destination_fixed_hour);

  // Date & Time card (#287): render each request date at its own precision in
  // the active language. Fixed dates keep the exact day; flexible (month-only)
  // values show the month and year only; an unspecified request has neither.
  const originDate = formatQuoteRequestDate(
    quote.date_type,
    quote.date_type === 'fixed' ? quote.origin_fixed_date : quote.origin_date,
    { language },
  );
  const destinationDate = formatQuoteRequestDate(
    quote.date_type,
    quote.date_type === 'fixed' ? quote.destination_fixed_date : quote.destination_date,
    { language },
  );

  return (
    <div className="space-y-4 pb-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Button
          variant="link"
          onClick={() => navigate('/dashboard')}
          className="border p-2 h-auto text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('quote.detail.back')}
        </Button>
        <span className="text-slate-300">/</span>
        <span className="text-slate-700 font-medium">{quote.tracking_number}</span>
      </div>

      {/* Title row */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl tracking-tight">
            {quote.origin_country} {ROUTE_ARROW} {quote.destination_country}
          </h1>
          <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {pickupDate ? (
              <span>{t('quote.detail.pickup_date', { date: pickupDate })} · </span>
            ) : isUnspecifiedDateType(quote.date_type) ? (
              <span>{t('quote.detail.dates.none')} · </span>
            ) : null}
            {t('quote.detail.age', { time: orderAge })}
          </p>
        </div>
        <Badge variant={getStatusBadgeVariant(quote.status)} className={`text-base px-4 py-2 shrink-0 ${getStatusBadgeClass(quote.status)}`}>
          {t(`quote.status.${quote.status}`)}
        </Badge>
      </div>

      {/* Response Counter */}
      {companiesContacted > 0 && (
        <p className="text-right text-slate-500">
          {t('quote.detail.response_counter', {
            count: companiesResponded,
            total: companiesContacted,
          })}
        </p>
      )}

      {/* Quote Responses — Expandable Rows */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle>
            {t('quote.detail.responses.title')} ({quote.quote_responses.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {quote.quote_responses.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              {t('quote.detail.responses.none')}
            </p>
          ) : (
            <div className="space-y-2">
              {sortedResponses.map((response, index) => {
                const isExpanded = expandedIds.has(response.id);
                const isMostRecent = index === 0;

                return (
                  <div
                    key={response.id}
                    className="border border-slate-200 rounded-lg overflow-hidden"
                  >
                    {/* Row header — clickable */}
                    <button
                      type="button"
                      data-testid={`proposal-toggle-${response.id}`}
                      onClick={() => toggleExpanded(response.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`proposal-details-${response.id}`}
                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 transition-colors"
                    >
                      {/* Proposal label + badge — carrier identity is withheld
                          from the customer-facing serializer (#280). */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm truncate">
                            {t('quote.detail.responses.proposal')}
                          </span>
                          {isMostRecent && (
                            <Badge variant="secondary" className="bg-sky-100 text-sky-700 text-[10px] px-1.5 py-0">
                              {t('quote.detail.responses.most_recent')}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {response.valid_until && (
                            <span>
                              {t('quote.detail.responses.valid_until')}: {format(new Date(response.valid_until), 'PP')} ·{' '}
                            </span>
                          )}
                          <span>
                            {formatDistanceToNow(new Date(response.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right shrink-0">
                        <span className="text-lg text-green-600">
                          {formatPrice(response.price, response.currency)}
                        </span>
                      </div>

                      {/* Chevron */}
                      <ChevronDown
                        data-testid={`chevron-${response.id}`}
                        className={`h-5 w-5 text-slate-400 shrink-0 transition-transform duration-250 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>

                    {/* Expandable content */}
                    <div
                      id={`proposal-details-${response.id}`}
                      role="region"
                      aria-label={t('quote.detail.responses.details_label')}
                      className="grid transition-all duration-250 ease-in-out"
                      style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
                    >
                      <div className="overflow-hidden">
                        <div className="px-3 pb-3 pt-1 border-t border-slate-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                            <div>
                              <p className="text-sm font-medium text-slate-500">{t('quote.detail.responses.price')}</p>
                              <p className="text-2xl text-green-600">
                                {formatPrice(response.price, response.currency)}
                              </p>
                            </div>
                            {response.estimated_delivery_date && (
                              <div>
                                <p className="text-sm font-medium text-slate-500">{t('quote.detail.responses.delivery')}</p>
                                <p>{format(new Date(response.estimated_delivery_date), 'PPP')}</p>
                              </div>
                            )}
                            {response.loading_date && (
                              <div>
                                <p className="text-sm font-medium text-slate-500">{t('quote.detail.responses.loading_date')}</p>
                                <p>{format(new Date(response.loading_date), 'PPP')}</p>
                              </div>
                            )}
                            {response.valid_until && (
                              <div>
                                <p className="text-sm font-medium text-slate-500">{t('quote.detail.responses.valid_until')}</p>
                                <div className="flex items-center gap-2">
                                  <p>{format(new Date(response.valid_until), 'PPP')}</p>
                                  {isExpired(response.valid_until) && (
                                    <Badge variant="destructive">{t('quote.detail.responses.expired')}</Badge>
                                  )}
                                  {!isExpired(response.valid_until) && isExpiringSoon(response.valid_until) && (
                                    <Badge variant="secondary">{t('quote.detail.responses.expires_soon')}</Badge>
                                  )}
                                </div>
                              </div>
                            )}
                            {response.observations && (
                              <div className="md:col-span-2">
                                <p className="text-sm font-medium text-slate-500">{t('quote.detail.observations.title')}</p>
                                <p className="text-sm text-slate-700 mt-1">{response.observations}</p>
                              </div>
                            )}
                            <div className="md:col-span-2">
                              <div className="flex items-center gap-2">
                                {response.contacted_at ? (
                                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                    {t('quote.detail.responses.contacted')} · {formatDistanceToNow(new Date(response.contacted_at), { addSuffix: true })}
                                  </Badge>
                                ) : response.contact_requested_at ? (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    disabled
                                    className="bg-slate-200 text-slate-500 hover:bg-slate-200 cursor-not-allowed"
                                  >
                                    {t('quote.detail.responses.awaiting_contact')}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handleContactCarrier(response.id)}
                                    disabled={contactingId === response.id}
                                  >
                                    {contactingId === response.id
                                      ? t('common.submitting')
                                      : t('quote.detail.responses.contact_carrier')
                                    }
                                  </Button>
                                )}
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleDownloadPdf(response.id)}
                                  disabled={downloadingResponseId === response.id}
                                >
                                  <Download className="h-4 w-4 mr-1" />
                                  {downloadingResponseId === response.id
                                    ? t('common.submitting')
                                    : t('quote.detail.responses.download_pdf')
                                  }
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Route Information */}
        <Card>
          <CardHeader className="py-4">
            <CardTitle>{t('quote.detail.route.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between p-4 pt-0 md:px-8">
            <div>
              <p className="text-sm font-medium text-slate-500">{t('quote.detail.route.origin')}</p>
              <p className="text-lg">{quote.origin_country}</p>
              <p className="text-sm text-slate-600">{quote.origin_zip_code}</p>
            </div>
            <div className="text-center text-2xl text-slate-400">{ROUTE_ARROW}</div>
            <div>
              <p className="text-sm font-medium text-slate-500">{t('quote.detail.route.destination')}</p>
              <p className="text-lg">{quote.destination_country}</p>
              <p className="text-sm text-slate-600">{quote.destination_zip_code}</p>
            </div>
          </CardContent>
        </Card>

        {/* Date & Time */}
        <Card>
          <CardHeader className="py-4">
            <CardTitle>{t('quote.detail.dates.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between p-4 pt-0 md:px-8">
            {quote.date_type === 'fixed' && (
              <>
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('quote.detail.dates.origin_date')}</p>
                  {originDate && (
                    <p>{originDate}</p>
                  )}
                  {originHour && (
                    <p className="text-sm text-slate-600">{t('quote.detail.dates.hour', { hour: originHour })}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('quote.detail.dates.destination_date')}</p>
                  {destinationDate && (
                    <p>{destinationDate}</p>
                  )}
                  {destinationHour && (
                    <p className="text-sm text-slate-600">{t('quote.detail.dates.hour', { hour: destinationHour })}</p>
                  )}
                </div>
              </>
            )}
            {quote.date_type === 'flexible' && (
              <>
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('quote.detail.dates.origin_date')}</p>
                  <p className="text-sm">
                    {originDate}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('quote.detail.dates.destination_date')}</p>
                  <p className="text-sm">
                    {destinationDate}
                  </p>
                </div>
              </>
            )}
            {isUnspecifiedDateType(quote.date_type) && (
              <p className="text-slate-500">{t('quote.detail.dates.none')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cargo Specifications */}
        <Card>
          <CardHeader className="py-4">
            <CardTitle>{t('quote.detail.cargo.title')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 pt-0">
            <div>
              <p className="text-sm font-medium text-slate-500">{t('quote.detail.cargo.dimensions')}</p>
              <p className="text-lg">
                {formatMeasurement(quote.cargo_length)} × {formatMeasurement(quote.cargo_width)} × {formatMeasurement(quote.cargo_height)} cm
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">{t('quote.detail.cargo.volume')}</p>
              <p className="text-lg">{formatCargoVolume(quote.cargo_volume_m3)} m³</p>
            </div>
            {quote.cargo_weight && (
              <div>
                <p className="text-sm font-medium text-slate-500">{t('quote.detail.cargo.weight')}</p>
                <p className="text-lg">{formatMeasurement(quote.cargo_weight)} kg</p>
              </div>
            )}
            <div className="md:col-span-3">
              <p className="text-sm font-medium text-slate-500 mb-2">{t('quote.detail.cargo.transport_methods')}</p>
              <div className="flex flex-wrap gap-2">
                {quote.transportation_methods.map((method) => (
                  <Badge key={method} variant="outline">
                    {t(`quote.methods.${method}`)}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader className="py-4">
            <CardTitle>{t('quote.detail.contact.title')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 pt-0">
            {quote.contact_name && (
              <div>
                <p className="text-sm font-medium text-slate-500">{t('quote.detail.contact.name')}</p>
                <p>{quote.contact_name}</p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-slate-500">{t('quote.detail.contact.email')}</p>
              <p>{quote.contact_email}</p>
            </div>
            {quote.contact_phone && (
              <div>
                <p className="text-sm font-medium text-slate-500">{t('quote.detail.contact.phone')}</p>
                <p>{quote.contact_phone}</p>
              </div>
            )}
            {quote.contact_company && (
              <div>
                <p className="text-sm font-medium text-slate-500">{t('quote.detail.contact.company')}</p>
                <p>{quote.contact_company}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Observations */}
      {quote.observations && (
        <Card>
          <CardHeader className="py-4">
            <CardTitle>{t('quote.detail.observations.title')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <p className="text-slate-700 whitespace-pre-wrap">{quote.observations}</p>
          </CardContent>
        </Card>
      )}

      {/* Cargo Images */}
      {quote.cargo_images.length > 0 && (
        <Card>
          <CardHeader className="py-4">
            <CardTitle>{t('quote.detail.images.title')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quote.cargo_images.map((image, index) => (
                <img
                  key={image.id}
                  src={image.thumb_url || image.url}
                  alt={image.filename}
                  className="w-full h-32 object-cover rounded cursor-pointer border hover:opacity-80 transition-opacity"
                  onClick={() => {
                    setCurrentImageIndex(index);
                    setImageModalOpen(true);
                  }}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-4xl">
          {quote.cargo_images.length > 0 && (
            <div className="relative">
              <img
                src={quote.cargo_images[currentImageIndex].url}
                alt={quote.cargo_images[currentImageIndex].filename}
                className="w-full max-h-[80vh] object-contain"
              />
              {quote.cargo_images.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute left-4 top-1/2 -translate-y-1/2"
                    onClick={prevImage}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                    onClick={nextImage}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {t('quote.detail.images.count', {
                      current: currentImageIndex + 1,
                      total: quote.cargo_images.length
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer Status Line */}
      {pendingCount > 0 && (
        <div className="text-center text-sm text-slate-500 border-t pt-4">
          {t('quote.detail.footer.waiting', { count: pendingCount })} · {t('quote.detail.footer.updated', { time: formatDistanceToNow(new Date(quote.updated_at), { addSuffix: true }) })}
        </div>
      )}
    </div>
  );
};
